/**
 * Files accepted as a Schematic Plan: one PDF (any number of pages) or one
 * JPG/PNG, up to 10 MB. The backend checks the same rules again, by the
 * file's first bytes; these checks only spare the reviewer a failed upload.
 */
export const SCHEMATIC_FILE_MAX_BYTES = 10 * 1024 * 1024;

export const SCHEMATIC_FILE_ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const ALLOWED_EXTENSIONS = ["pdf", "jpg", "jpeg", "png"];

/** Why the file cannot be uploaded, or null when it can. */
export function schematicFileProblem(file: Pick<File, "name" | "size" | "type">): string | null {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (!ALLOWED_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(extension)) {
    return "Only PDF, JPG and PNG files are allowed.";
  }
  if (file.size > SCHEMATIC_FILE_MAX_BYTES) return "The file is larger than 10 MB.";
  if (!file.size) return "The file is empty.";
  return null;
}

export const SCHEMATIC_IMAGE_ACCEPT = ".jpg,.jpeg,.png,image/jpeg,image/png";

const IMAGE_TYPES = ["image/jpeg", "image/png"];
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png"];

/**
 * Why the plot image of "Upload image + dimensions" cannot be uploaded, or
 * null when it can: one JPG or PNG up to 10 MB. The backend checks again.
 */
export function schematicImageProblem(file: Pick<File, "name" | "size" | "type">): string | null {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (!IMAGE_TYPES.includes(file.type) && !IMAGE_EXTENSIONS.includes(extension)) {
    return "Only JPG and PNG images are allowed.";
  }
  if (file.size > SCHEMATIC_FILE_MAX_BYTES) return "The file is larger than 10 MB.";
  if (!file.size) return "The file is empty.";
  return null;
}

/** "2.4 MB" / "850 KB". */
export function formatFileSize(bytes: number | undefined): string {
  if (!bytes) return "";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
